import React, { useState, useEffect } from 'react';
import { Radar } from 'lucide-react';
import { auth } from '../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../config/Config';
import {
  watchLibraryItems,
  approveLibraryItem,
  deleteLibraryItem,
  updateLibraryItem,
} from '../../services/LibraryRepository.js';
import LibraryAdminList from './components/LibraryAdminList';
import ZoteroEditModal from '../zotero/ZoteroEditModal';
import { LegalDisclaimer } from '../../components/LegalDisclaimer';

const API_BASE = (String(import.meta?.env?.VITE_BACKEND_URL || '').trim() || 'https://rda-backend-thuq.onrender.com').replace(/\/+$/, '');
const getAuthHeaders = async () => {
  try {
    const token = await getToken();
    return token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};
const appId = FIREBASE_CONFIG.projectId;

export default function LibraryManager({ radarTrigger = 0 }) {
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('TRIAGE'); // 'PUBLISHED' ou 'TRIAGE'
  const [editingItem, setEditingItem] = useState(null);
  const [isHunting, setIsHunting] = useState(false);
  const [huntMsg, setHuntMsg] = useState('');

  useEffect(() => {
    if (radarTrigger > 0) handleHunt();
  }, [radarTrigger]);

  const handleHunt = async () => {
    if (isHunting) return;
    setIsHunting(true);
    setHuntMsg('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/zotero/hunt`, { method: 'POST', headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setHuntMsg('✅ Radar acionado. Os resultados aparecerão na Triagem em breve.');
      } else {
        setHuntMsg(`❌ ${String(data?.detail || 'Erro ao acionar o Radar.')}`);
      }
    } catch (err) {
      setHuntMsg(`❌ Falha de conexão: ${err.message}`);
    } finally {
      setIsHunting(false);
      setTimeout(() => setHuntMsg(''), 7000);
    }
  };

  useEffect(() => {
    const unsub = watchLibraryItems((items) => setItems(items));
    return () => unsub();
  }, []);

  const filteredItems = items.filter(i => {
    if (activeTab === 'TRIAGE') return i.status === 'PENDING_REVIEW';
    return i.status === 'PUBLISHED';
  });

  const handleApprove = async (item) => {
    if (window.confirm(`Aprovar e Publicar "${item.title}"?`)) {
        await approveLibraryItem(item.id);
    }
  };

  const handleDelete = async (id) => {
    await deleteLibraryItem(id);
  };

  const handleSave = async (data) => {
    if (editingItem) {
        await updateLibraryItem(editingItem.id, data);
        setEditingItem(null);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-2.5">
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button onClick={() => setActiveTab('TRIAGE')} className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'TRIAGE' ? 'bg-white shadow text-[#C5A059]' : 'text-gray-500'}`}>
            Triagem ({items.filter(i => i.status === 'PENDING_REVIEW').length})
          </button>
          <button onClick={() => setActiveTab('PUBLISHED')} className={`px-4 py-2 text-xs font-bold rounded-md transition-all ${activeTab === 'PUBLISHED' ? 'bg-white shadow text-[#20243B]' : 'text-gray-500'}`}>
            Publicados ({items.filter(i => i.status === 'PUBLISHED').length})
          </button>
        </div>
        <button
          onClick={handleHunt}
          disabled={isHunting}
          className="flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg bg-[#20243B] text-[#C5A059] border border-[#C5A059]/40 hover:bg-[#2d3352] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Radar size={14} className={isHunting ? 'animate-spin' : ''} />
          {isHunting ? 'A varrer a internet...' : 'Acionar Radar'}
        </button>
      </div>

      {huntMsg && (
        <div className={`text-xs font-medium px-4 py-2 rounded-lg border ${
          huntMsg.startsWith('✅') ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
        }`}>{huntMsg}</div>
      )}

      <LegalDisclaimer className="mb-4" />

      <LibraryAdminList 
        items={filteredItems} 
        onDelete={handleDelete} 
        onEdit={setEditingItem} 
        onApprove={activeTab === 'TRIAGE' ? handleApprove : null}
      />

      <ZoteroEditModal 
        isOpen={!!editingItem} 
        initialData={editingItem} 
        onClose={() => setEditingItem(null)} 
        onSave={handleSave} 
      />
    </div>
  );
}